<script setup>
import { computed, ref, watch } from 'vue';
import { useMessageContext } from 'dashboard/components-next/message/provider.js';
import {
  CONTENT_TYPES,
  MESSAGE_TYPES,
  ORIENTATION,
} from 'dashboard/components-next/message/constants';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessageReactionsAPI from 'custom/api/messageReactions';
import ConversationToolsAPI from 'custom/api/conversationTools';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import AiTrace from 'custom/ai/AiTrace.vue';

// Wraps every message bubble (see components-next/message/Message.vue) with what the fork adds around it:
// the participant's name in group chats, reactions, editing my own messages, edited / deleted / forwarded markers,
// and what an AI assistant's answer was based on.
const { t } = useLocoI18n();
const {
  id,
  conversationId,
  content,
  contentType,
  contentAttributes,
  messageType,
  sender,
  orientation,
  isPrivate,
} = useMessageContext();
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
// Edit and react buttons next to the bubble: shown on hover, always on touch screens (no hover there)
const ACTION_BUTTON =
  'opacity-0 group-hover/frame:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity size-8 p-0 rounded-full flex items-center justify-center flex-shrink-0 self-center bg-n-solid-2 text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak shadow-sm hover:bg-n-alpha-2 disabled:opacity-50';

const showPicker = ref(false);
const showHistory = ref(false);
const isReacting = ref(false);
const localReactions = ref(null);
const isEditing = ref(false);
const isSavingEdit = ref(false);
const draft = ref('');

const attributes = computed(() => contentAttributes.value || {});
const isRight = computed(() => orientation.value === ORIENTATION.RIGHT);

// In group conversations the contact is the group; a different contact sender is the participant
const groupSenderName = computed(() => {
  const chatContactId = currentChat.value?.meta?.sender?.id;
  const messageSender = sender.value;
  if (!messageSender || messageSender.type !== 'contact' || !chatContactId)
    return '';
  return messageSender.id === chatContactId ? '' : messageSender.name;
});

const reactions = computed(
  () => localReactions.value ?? attributes.value.reactions ?? []
);
const groupedReactions = computed(() => {
  const groups = {};
  reactions.value.forEach(reaction => {
    groups[reaction.emoji] ||= {
      emoji: reaction.emoji,
      names: [],
      mine: false,
    };
    groups[reaction.emoji].names.push(reaction.reactorName);
    if (
      reaction.reactorType === 'User' &&
      String(reaction.reactorId) === String(currentUser.value?.id)
    ) {
      groups[reaction.emoji].mine = true;
    }
  });
  return Object.values(groups);
});
const myReaction = computed(
  () => groupedReactions.value.find(group => group.mine)?.emoji || ''
);

const editHistory = computed(() => attributes.value.editHistory || []);
const aiTrace = computed(() => {
  const trace = attributes.value.locoAi;
  if (!trace) return null;
  return {
    sources: trace.sources || [],
    toolCalls: trace.toolCalls || [],
    model: trace.model || '',
    tokens: trace.tokens || null,
    cost: trace.cost ?? null,
  };
});
const deletedLabel = computed(() => {
  const by = attributes.value.whatsappDeletedBy;
  if (!by) return '';
  return by === 'contact'
    ? t('LOCO.WHATSAPP.MESSAGE.DELETED_BY_CONTACT')
    : t('LOCO.WHATSAPP.MESSAGE.DELETED_BY_BUSINESS');
});
const markers = computed(() =>
  [
    attributes.value.whatsappForwarded && t('LOCO.WHATSAPP.MESSAGE.FORWARDED'),
    attributes.value.whatsappViewOnce && t('LOCO.WHATSAPP.MESSAGE.VIEW_ONCE'),
    attributes.value.mediaUnavailable &&
      t('LOCO.WHATSAPP.MESSAGE.MEDIA_UNAVAILABLE'),
  ].filter(Boolean)
);
// Server updates (ours echoed back, or someone else's reaction) replace the optimistic copy
watch(
  () => attributes.value.reactions,
  () => {
    localReactions.value = null;
  }
);

const canReact = computed(
  () => !isPrivate.value && Boolean(conversationId.value)
);

// My own text replies and notes; the server decides whether the channel still allows it
const canEdit = computed(
  () =>
    Boolean(conversationId.value) &&
    sender.value?.type === 'user' &&
    sender.value?.id === currentUser.value?.id &&
    (contentType.value || CONTENT_TYPES.TEXT) === CONTENT_TYPES.TEXT &&
    (isPrivate.value || messageType.value === MESSAGE_TYPES.OUTGOING) &&
    !attributes.value.deleted
);

const startEditing = () => {
  draft.value = content.value || '';
  isEditing.value = true;
};

const saveEdit = async () => {
  if (!draft.value.trim() || draft.value === content.value) {
    isEditing.value = false;
    return;
  }
  isSavingEdit.value = true;
  try {
    await ConversationToolsAPI.editMessage(
      conversationId.value,
      id.value,
      draft.value
    );
    isEditing.value = false;
  } catch (error) {
    useAlert(
      t('LOCO.EDIT.ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isSavingEdit.value = false;
  }
};

const react = async emoji => {
  showPicker.value = false;
  const next = emoji === myReaction.value ? '' : emoji;
  isReacting.value = true;
  try {
    const { data } = await MessageReactionsAPI.react(
      conversationId.value,
      id.value,
      next
    );
    localReactions.value = data.reactions.map(reaction => ({
      emoji: reaction.emoji,
      reactorType: reaction.reactor_type,
      reactorId: reaction.reactor_id,
      reactorName: reaction.reactor_name,
    }));
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('LOCO.WHATSAPP.MESSAGE.REACTION_ERROR')
    );
  } finally {
    isReacting.value = false;
  }
};
</script>

<template>
  <div
    class="group/frame flex flex-col min-w-0 max-w-full gap-1"
    :class="isRight ? 'items-end' : 'items-start'"
  >
    <span
      v-if="groupSenderName"
      class="px-1 text-xs font-medium text-n-slate-11 truncate max-w-full"
    >
      {{ groupSenderName }}
    </span>

    <div
      class="relative flex items-center gap-1.5 min-w-0 max-w-full"
      :class="isRight ? 'flex-row-reverse' : 'flex-row'"
    >
      <slot />
      <button
        v-if="canEdit && !isEditing"
        type="button"
        :class="ACTION_BUTTON"
        :title="t('LOCO.EDIT.EDIT')"
        @click="startEditing"
      >
        <Icon icon="i-lucide-pencil" class="size-5 flex-shrink-0" />
      </button>
      <div v-if="canReact" class="relative self-center flex-shrink-0">
        <button
          type="button"
          :class="ACTION_BUTTON"
          :title="t('LOCO.WHATSAPP.MESSAGE.ADD_REACTION')"
          :disabled="isReacting"
          @click="showPicker = !showPicker"
        >
          <Icon icon="i-lucide-smile-plus" class="size-5 flex-shrink-0" />
        </button>
        <div
          v-if="showPicker"
          class="absolute z-10 bottom-9 flex gap-1 p-1 rounded-full bg-n-solid-2 shadow-md outline outline-1 outline-n-weak"
          :class="isRight ? 'right-0' : 'left-0'"
        >
          <button
            v-for="emoji in QUICK_REACTIONS"
            :key="emoji"
            type="button"
            class="size-7 rounded-full text-base hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': emoji === myReaction }"
            @click="react(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
      </div>
    </div>

    <form
      v-if="isEditing"
      class="flex flex-col gap-2 w-full max-w-md"
      @submit.prevent="saveEdit"
    >
      <textarea
        v-model="draft"
        rows="3"
        class="!mb-0 text-sm resize-y"
        @keydown.esc="isEditing = false"
        @keydown.enter.meta.prevent="saveEdit"
        @keydown.enter.ctrl.prevent="saveEdit"
      />
      <div
        class="flex gap-2"
        :class="isRight ? 'justify-end' : 'justify-start'"
      >
        <button
          type="button"
          class="px-2 h-7 rounded-md text-xs text-n-slate-11 hover:bg-n-alpha-2"
          @click="isEditing = false"
        >
          {{ t('LOCO.EDIT.CANCEL') }}
        </button>
        <button
          type="submit"
          class="px-3 h-7 rounded-md text-xs text-white bg-n-brand disabled:opacity-50"
          :disabled="isSavingEdit"
        >
          {{ t('LOCO.EDIT.SAVE') }}
        </button>
      </div>
    </form>

    <div
      v-if="groupedReactions.length"
      class="flex flex-wrap gap-1 -mt-1"
      :class="isRight ? 'justify-end' : 'justify-start'"
    >
      <button
        v-for="group in groupedReactions"
        :key="group.emoji"
        type="button"
        class="flex items-center gap-1 h-6 px-1.5 rounded-full text-xs outline outline-1 -outline-offset-1"
        :class="
          group.mine
            ? 'bg-n-brand/10 outline-n-brand text-n-blue-11'
            : 'bg-n-solid-2 outline-n-weak text-n-slate-11'
        "
        :title="group.names.filter(Boolean).join(', ')"
        :disabled="!canReact || isReacting"
        @click="react(group.emoji)"
      >
        <span>{{ group.emoji }}</span>
        <span v-if="group.names.length > 1">{{ group.names.length }}</span>
      </button>
    </div>

    <AiTrace v-if="aiTrace" v-bind="aiTrace" class="px-1" />

    <div
      v-if="attributes.editedAt || deletedLabel || markers.length"
      class="flex flex-wrap items-center gap-2 px-1 text-xs text-n-slate-10"
      :class="isRight ? 'justify-end' : 'justify-start'"
    >
      <span v-for="marker in markers" :key="marker">{{ marker }}</span>
      <span v-if="deletedLabel" class="flex items-center gap-1 text-n-ruby-11">
        <Icon icon="i-lucide-trash-2" class="size-3" />
        {{ deletedLabel }}
      </span>
      <button
        v-if="attributes.editedAt"
        type="button"
        class="flex items-center gap-1 hover:text-n-slate-12"
        @click="showHistory = !showHistory"
      >
        <Icon icon="i-lucide-pencil" class="size-3" />
        {{ t('LOCO.WHATSAPP.MESSAGE.EDITED') }}
      </button>
    </div>

    <div
      v-if="showHistory && editHistory.length"
      class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1 text-sm text-n-slate-11 max-w-md"
    >
      <span class="text-xs font-medium text-n-slate-12">
        {{ t('LOCO.WHATSAPP.MESSAGE.EDIT_HISTORY') }}
      </span>
      <div
        v-for="(version, index) in [...editHistory].reverse()"
        :key="index"
        class="flex flex-col"
      >
        <span
          class="whitespace-pre-wrap break-words line-through decoration-n-slate-8"
        >
          {{ version.content }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ new Date(version.replacedAt).toLocaleString() }}
        </span>
      </div>
    </div>
  </div>
</template>
